import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSectionNavigation } from '../../hooks/useSectionNavigation';
import { useCollection } from '../../hooks/useFirestore';
import { trackEvent } from '../../analytics/tracker';
import { Template } from '../../types';
import PreviewModal from '../ui/PreviewModal';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';

const Showcase: React.FC = () => {
  const { t } = useLanguage();
  const { data: allTemplates, loading } = useCollection<Template>('templates');
  const templates = allTemplates.filter(item => item.status !== 'archived');
  const [activeCategory, setActiveCategory] = useState('All');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const { scrollToSection } = useSectionNavigation();

  const categories = ['All', ...Array.from(new Set(templates.map(item => item.category)))];
  const filteredTemplates = activeCategory === 'All'
    ? templates
    : templates.filter(item => item.category === activeCategory);

  return (
    <section id="websites" className="sw-section" role="region" aria-labelledby="showcase-title">
      <div className="sw-container">
        <div className="section-header text-center">
          <span className="sw-eyebrow">{t('templates_eyebrow')}</span>
          <h2 id="showcase-title">{t('templates_title')}</h2>
          <p className="section-desc">{t('services_desc')}</p>
        </div>

        <div className="filter-wrap justify-center mb-10">
          {categories.map(cat => (
            <button
              key={cat}
              className={`filter-btn ${activeCategory === cat ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat)}
            >
              <span className="btn-label">{cat}</span>
              <span className="btn-count">
                {cat === 'All' ? templates.length : templates.filter(t => t.category === cat).length}
              </span>
            </button>
          ))}
        </div>

        <div className="showcase-grid sw-grid">
          {loading ? (
            Array(3).fill(0).map((_, i) => (
              <div key={i} className="sw-card skeleton-card">
                <div className="sw-skeleton" style={{ height: '180px', borderRadius: '12px', marginBottom: '20px' }}></div>
                <div className="sw-skeleton" style={{ height: '24px', width: '60%', marginBottom: '10px' }}></div>
                <div className="sw-skeleton" style={{ height: '16px', width: '80%' }}></div>
              </div>
            ))
          ) : (
            filteredTemplates.map((template, i) => (
              <motion.article
                key={template.id}
                className="sw-card template-card group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{ y: -4 }}
              >
                <div className="template-media">
                  <img src={template.thumbnail || '/src/assets/icons/1.jpg'} alt={template.name} loading="lazy" />
                  <div className="template-overlay">
                    <button
                      className="sw-btn sw-btn-primary"
                      onClick={() => {
                        setPreviewUrl(template.preview);
                        void trackEvent('sample_view', undefined, {
                          templateId: template.id,
                          name: template.name,
                          category: template.category
                        });
                      }}
                    >
                      <ExternalLink size={16} />
                      Live Preview
                    </button>
                  </div>
                </div>
                <div className="template-body">
                  <div className="template-head">
                    <h3>{template.name}</h3>
                    <span className="template-cat">{template.category}</span>
                  </div>
                  <p className="text-soft" style={{ fontSize: '0.95rem' }}>{template.description}</p>
                  <div style={{ marginTop: 'var(--sp-4)' }}>
                    <a
                      href="#contact"
                      className="sw-btn sw-btn-secondary btn-full"
                      onClick={(e) => {
                        void trackEvent('cta_click', undefined, { label: 'get_started', location: 'sample', templateId: template.id });
                        scrollToSection(e, 'contact');
                      }}
                    >
                      {t('btn_get_started')}
                    </a>
                  </div>
                </div>
              </motion.article>
            ))
          )}
        </div>
      </div>
      <PreviewModal url={previewUrl || ''} isOpen={!!previewUrl} onClose={() => setPreviewUrl(null)} />
    </section>
  );
};

export default Showcase;
